import React from 'react'
import { Input } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { apiFetch } from '../../../lib/api-client'

interface BrandingFields {
  logo_url: string | null
  show_logo_contracts: boolean
  show_logo_inspections: boolean
}

interface BrandingTabProps {
  companyId: string
  initial: BrandingFields
  onSaved: (updated: BrandingFields) => void
}

export function BrandingTab({ companyId, initial, onSaved }: BrandingTabProps) {
  const [logoUrl, setLogoUrl] = React.useState(initial.logo_url ?? '')
  const [showContracts, setShowContracts] = React.useState(initial.show_logo_contracts)
  const [showInspections, setShowInspections] = React.useState(initial.show_logo_inspections)
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await apiFetch<BrandingFields>(`/api/v1/companies/${companyId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          logo_url: logoUrl.trim() || null,
          show_logo_contracts: showContracts,
          show_logo_inspections: showInspections,
        }),
      })
      onSaved(updated)
      setSaved(true)
    } catch {
      setError('Kunne ikke gemme. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ maxWidth: 520, padding: '24px 28px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Branding</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          Selskabslogo og opsætning for logo på dokumenter. Indsæt en direkte URL til dit logo (JPG, PNG, SVG).
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <Input
            label="Logo URL"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.dk/logo.png"
          />
        </div>

        {logoUrl && (
          <div style={{ marginBottom: 20, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e8ecf4' }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>Forhåndsvisning</div>
            <img
              src={logoUrl}
              alt="Logo"
              style={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>Vis logo på</div>
          {[
            { label: 'Lejekontrakter', checked: showContracts, onChange: setShowContracts },
            { label: 'Flytningsrapporter', checked: showInspections, onChange: setShowInspections },
          ].map(({ label, checked, onChange }) => (
            <label
              key={label}
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(ev) => onChange(ev.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#4f46e5', cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, color: '#0f172a' }}>{label}</span>
            </label>
          ))}
        </div>

        {error && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>}
        {saved && <p style={{ fontSize: 12, color: '#16a34a', marginBottom: 12 }}>Gemt.</p>}

        <Button type="submit" isLoading={saving}>Gem</Button>
      </form>
    </div>
  )
}
